/**
 * Ion chat streaming — POST /api/v1/chat/stream and consume the NDJSON
 * response.
 *
 * Two surfaces share this file:
 *
 *   1) Admin-side, self-contained hook `useChatStream({persistConversation})`
 *      and companions `useChatModels()` / `useChatConversations()`. State is
 *      local to the component (used by AdminAiTab dev sandbox).
 *
 *   2) Customer-facing imperative `sendChatMessage()` / `stopChatStream()` that
 *      dispatches into the global `chatStore` signals. Used by the slide-over
 *      Launcher and the fullpage `/assistant` route.
 *
 * Both surfaces speak the same wire protocol (`shared/chat-events.schema.json`
 * v0.1) and consume the same NDJSON parser. Forward-compat: unknown event
 * types are logged + ignored.
 *
 * Why fetch + ReadableStream (not SSE / EventSource)?
 *   EventSource cannot send Authorization headers, so JWT auth would force
 *   either query-param tokens (insecure) or cookie auth (would conflict with
 *   the rest of the app). Fetch with a stream body works for POST and
 *   integrates cleanly with AbortController.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { authenticatedFetch } from '../services/authService.js';
import { recordAccessDenial } from '../services/ionAccessService.js';
import { t } from '../i18n/index.js';
import { getApiBase } from '../components/admin/analytics/shared/formatters.js';
import { isDemoActive, streamDemoEvents } from '../services/ionDemoMode.js';
import { CAPABILITY_RECORDINGS } from '../services/ionCapabilityRecordings.js';
import { mapApiMessage } from '../services/chatMessageMapper.js';
import {
  applyContextCommand,
  applyViewCommand,
  applyPlotCommand,
  applyTableColumnsCommand,
  applyFilterCommand,
  beginIonTransaction,
  commitIonTransaction,
  rollbackOpenTransaction,
} from '../state/commandLayer.js';
import {
  abortController,
  appendDelta,
  appendUserMessage,
  attachSource,
  startAssistantMessage,
  startToolCall,
  updateToolCallInput,
  endToolCall,
  addFigure,
  addKpiGroup,
  markDone,
  markError,
  markAborted,
  updateQuota,
  applyLiveQuotaSnapshot,
  setLastUsedModel,
  currentConversationId,
  pinLatestArtifactFromMessages,
  chatMessages,
} from '../state/chatStore.js';

const CHAT_STREAM_URL = '/api/v1/chat/stream';
const CONVO_STORAGE_KEY = 'batemo.ai.conversationId';

// How long the customer-facing stream may go without a single byte arriving
// before we treat it as stalled. A stalled upstream used to leave the message
// in `streaming` until the user hit Stop by hand. Purely client-side — no
// protocol change, nothing new on the wire. Overridable per call
// (`sendChatMessage(text, { idleTimeoutMs })`) so tests can drive it with fake
// timers; 0 or a negative value disables the watchdog.
//
// While a turn is alive the backend sends a bare-newline heartbeat after every
// 15 s of silence (`with_heartbeat`, backend/app/api/v1/chat/streaming.py). It
// is no event, but `parseNdjsonStream` counts it as activity, so a turn that
// thinks or runs a tool for minutes keeps this watchdog reset. What it detects
// is a dead connection or a dead backend, not a slow turn. 120 s is a wide
// margin over the 15 s heartbeat interval.
export const STREAM_IDLE_TIMEOUT_MS = 120_000;

// Events we know how to route. Anything not in this set is logged + ignored
// (forward-compat — backend can add events without breaking the UI).
const KNOWN_EVENTS = new Set([
  'text-delta',
  'data-source',
  'tool-input-start',
  'tool-input-available',
  'tool-output-available',
  'figure',
  'kpi-group',
  'quota',
  'usage',
  'finish',
  'error',
]);

function newId() {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Explorer commands (Ion-as-Insights Phase 1) — the backend's explorer_command
// tool drives the Explorer UI through the Task-2 command layer
// (state/commandLayer.js). Wired into the LIVE dispatch loop only (see
// dispatchEvent's tool-output-available case below); replaying history never
// re-executes a command because loadConversationIntoStore
// (services/ionConversationService.js) rehydrates via mapApiMessage and never
// calls dispatchEvent — the guard is structural, not a flag.
// ---------------------------------------------------------------------------

// Tracks which assistant messageIds already opened an Ion transaction, so the
// FIRST explorer_command of an assistant turn opens it and every subsequent
// command in that same turn applies directly — the whole turn reverts as one
// unit. Pruned by the `finish`/`error` handling below (Task 10), which is
// also where the transaction commits (with a label) or reverts.
const ionTransactionStartedFor = new Set();

// Tallies the explorer_commands dispatched in the current turn, keyed by
// messageId, so the `finish` handler can build a human commit label (e.g.
// "3 filters + sort") without re-deriving it from the raw command stream.
// Pruned alongside ionTransactionStartedFor once the turn commits/reverts.
const ionCommandTally = new Map();

function tallyExplorerCommand(messageId, { command, payload } = {}) {
  const tally = ionCommandTally.get(messageId) || {
    filters: 0,
    viewChanged: false,
    sortChanged: false,
    contextChanged: false,
    plotChanged: false,
    columnsChanged: false,
  };
  switch (command) {
    case 'set_filters': {
      const facetCount = Object.values(payload?.facets ?? {}).reduce(
        (n, v) => n + (Array.isArray(v) ? v.length : 1),
        0
      );
      const rangeCount = (payload?.ranges ?? []).length;
      tally.filters += facetCount + rangeCount;
      break;
    }
    case 'set_view':
      if (payload?.view) tally.viewChanged = true;
      if (payload?.sortBy || payload?.sortOrder) tally.sortChanged = true;
      break;
    case 'set_plot':
      // Counts as "plot", NOT as "view", even though the payload carries
      // view:'graph'. The label describes what the user asked for, and they
      // asked for a chart — "view + plot" would read as two separate changes.
      tally.plotChanged = true;
      break;
    case 'set_table_columns':
      // Same reasoning as set_plot: the payload carries view:'table', but the
      // user asked for columns, not for a view change.
      tally.columnsChanged = true;
      break;
    case 'set_context':
      tally.contextChanged = true;
      break;
  }
  ionCommandTally.set(messageId, tally);
}

/**
 * Build the commit label from this turn's tallied commands, e.g.
 * "3 filters + sort" or "view + context". Falls back to a generic label if a
 * transaction opened but nothing tallied (shouldn't normally happen).
 */
function buildIonCommitLabel(messageId) {
  const tally = ionCommandTally.get(messageId);
  if (!tally) return 'Ion update';
  const parts = [];
  if (tally.filters > 0) parts.push(`${tally.filters} filter${tally.filters === 1 ? '' : 's'}`);
  if (tally.viewChanged) parts.push('view');
  if (tally.sortChanged) parts.push('sort');
  if (tally.plotChanged) parts.push('plot');
  if (tally.columnsChanged) parts.push('columns');
  if (tally.contextChanged) parts.push('context');
  return parts.length ? parts.join(' + ') : 'Ion update';
}

/**
 * If an Ion transaction is still open for this messageId, commit it and
 * clear the turn's bookkeeping — same commit path `finish` uses. Used by
 * BOTH the `finish` handler and the abort paths below (C2 fix): when a turn
 * is aborted (Stop button) before a `finish`/`error` event arrives, its
 * snapshot would otherwise stay stranded on the commandLayer
 * `transactionStack`, where a LATER turn's Revert would wrongly pop it
 * instead of that later turn's own snapshot. Committing (not reverting) is
 * correct here — the user already SEES the filters Ion applied before the
 * abort, so auto-reverting on Stop would make the view jump.
 */
function commitIonTransactionIfOpen(messageId) {
  if (ionTransactionStartedFor.has(messageId)) {
    commitIonTransaction(buildIonCommitLabel(messageId));
    ionTransactionStartedFor.delete(messageId);
    ionCommandTally.delete(messageId);
  }
}

/**
 * Route one unwrapped explorer_command `{command, payload}` to the matching
 * command-layer function.
 */
function executeExplorerCommand({ command, payload } = {}) {
  switch (command) {
    case 'set_context':
      applyContextCommand(payload || {});
      break;

    case 'set_view':
      applyViewCommand(payload || {});
      break;

    case 'set_plot':
      applyPlotCommand(payload || {});
      break;

    case 'set_table_columns':
      applyTableColumnsCommand(payload || {});
      break;

    case 'set_filters': {
      // Backend emits `payload.ranges` as a LIST ([{metric,min,max,basis}]),
      // but applyFilterCommand expects an OBJECT keyed by metric
      // (verified 2026-07-21, see .superpowers/sdd/progress.md Task 7 note).
      // Facets pass through as-is — applyFilterCommand does the
      // chemistry→cathode-chemistry mapping itself.
      const ranges = {};
      for (const r of payload?.ranges ?? []) {
        ranges[r.metric] = { min: r.min, max: r.max, basis: r.basis };
      }
      applyFilterCommand({ merge: payload?.merge, facets: payload?.facets, ranges });
      break;
    }

    default:
      console.info('[useChatStream] ignoring unknown explorer_command:', command);
  }
}

function readConversationId(persist) {
  if (!persist) return null;
  try {
    return sessionStorage.getItem(CONVO_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeConversationId(id, persist) {
  if (!persist) return;
  try {
    sessionStorage.setItem(CONVO_STORAGE_KEY, id);
  } catch {
    // Quota or privacy-mode — silently ignore.
  }
}

// ---------------------------------------------------------------------------
// Shared NDJSON parser (used by both surfaces)
// ---------------------------------------------------------------------------

/**
 * Parse an NDJSON byte stream into discrete JSON events. Yields each parsed
 * event in order. Handles arbitrary chunk boundaries — a single line may be
 * split across multiple reads, and a single read may contain many lines.
 *
 * Exported so unit tests can hammer it with weird chunking.
 *
 * `onActivity` (optional) is invoked once per successfully read chunk, before
 * the chunk is parsed — the hook point the customer-side inactivity watchdog
 * resets itself from. It must not throw.
 */
export async function* parseNdjsonStream(reader, { onActivity } = {}) {
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (onActivity) onActivity();
    buffer += decoder.decode(value, { stream: true });

    let nlIdx;
    while ((nlIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nlIdx).trim();
      buffer = buffer.slice(nlIdx + 1);
      if (!line) continue;
      try {
        yield JSON.parse(line);
      } catch (e) {
        console.warn('[useChatStream] discarding malformed NDJSON line:', line, e);
      }
    }
  }

  // Flush remaining tail (in case the stream ends without a trailing newline)
  const tail = buffer.trim();
  if (tail) {
    try {
      yield JSON.parse(tail);
    } catch (e) {
      console.warn('[useChatStream] discarding malformed NDJSON tail:', tail, e);
    }
  }
}

// ---------------------------------------------------------------------------
// Customer-facing imperative API — dispatches to global chatStore
// ---------------------------------------------------------------------------

// Animation-frame batched flush. We accumulate text deltas across frames
// to avoid one DOM mutation per token (which would cause flicker on fast
// streams).
function createDeltaBuffer() {
  let pending = new Map();
  let rafHandle = null;

  function flush() {
    rafHandle = null;
    for (const [messageId, text] of pending) {
      if (text) appendDelta(messageId, text);
    }
    pending.clear();
  }

  function schedule(messageId, delta) {
    const prev = pending.get(messageId) || '';
    pending.set(messageId, prev + delta);
    if (rafHandle == null) {
      rafHandle =
        typeof requestAnimationFrame !== 'undefined'
          ? requestAnimationFrame(flush)
          : setTimeout(flush, 16);
    }
  }

  function forceFlush() {
    if (rafHandle != null) {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafHandle);
      } else {
        clearTimeout(rafHandle);
      }
      rafHandle = null;
    }
    flush();
  }

  return { schedule, forceFlush };
}

/**
 * Apply a single parsed event to the chat store. Unknown event types are
 * logged and ignored (forward-compat).
 */
export function dispatchEvent(event, { messageId, deltaBuffer }) {
  if (!event || typeof event !== 'object') return;

  switch (event.type) {
    case 'text-delta':
      // event.id refers to the *message* id from the backend; we ignore it
      // and append to the assistant message we created locally. This avoids
      // races when the backend reuses ids across reconnects.
      deltaBuffer.schedule(messageId, event.delta || '');
      break;

    case 'data-source':
      // Flush pending text deltas FIRST so the source placeholder lands
      // at the correct position relative to the streamed text.
      deltaBuffer.forceFlush();
      attachSource(messageId, {
        cellId: event.cell_id,
        value: event.value,
        unit: event.unit ?? null,
        label: event.label ?? null,
      });
      break;

    case 'tool-input-start':
      // Flush pending text deltas FIRST so the [[TOOL:id]] placeholder
      // lands at the correct chronological position in the assistant
      // text — same reason data-source / kpi-group / figure flush.
      deltaBuffer.forceFlush();
      startToolCall(messageId, {
        toolCallId: event.tool_call_id,
        toolName: event.tool_name,
        input: event.input || {},
        // Customer-safe localisation descriptor ({key, params}) — rendered
        // as the human status line. Optional (older backends omit it).
        status: event.status || null,
      });
      break;

    case 'tool-input-available':
      updateToolCallInput(messageId, event.tool_call_id, event.input || {});
      break;

    case 'tool-output-available': {
      endToolCall(messageId, event.tool_call_id, {
        output: event.output,
        isError: !!event.is_error,
      });

      // Explorer commands (Ion-as-Insights P1). Envelope-aware unwrap — the
      // tool executor wraps successful returns as {result: ...} (same shape
      // MarkdownRenderer/chatStore already unwrap for artifact_type), but a
      // flat shape is accepted too.
      if (!event.is_error) {
        const output = event.output;
        const art = output?.artifact_type ?? output?.result?.artifact_type;
        if (art === 'explorer_command') {
          const cmd = output?.result ?? output;
          if (!ionTransactionStartedFor.has(messageId)) {
            ionTransactionStartedFor.add(messageId);
            beginIonTransaction();
          }
          tallyExplorerCommand(messageId, cmd);
          executeExplorerCommand(cmd);
        }
      }
      break;
    }

    case 'figure':
      deltaBuffer.forceFlush();
      addFigure(messageId, {
        url: event.url,
        caption: event.caption ?? null,
        alt: event.alt ?? null,
        width: event.width ?? null,
        height: event.height ?? null,
      });
      break;

    case 'kpi-group':
      deltaBuffer.forceFlush();
      addKpiGroup(messageId, {
        id: event.id,
        kpis: event.kpis || [],
      });
      break;

    case 'quota':
      // Legacy top-level daily whole-token fields → the percentage bucket
      // (`tokenBudget`), unchanged.
      updateQuota({
        used: event.used,
        limit: event.limit,
        resetAt: event.reset_at,
      });
      // Live meter (2026-07-17): the additive per-window token snapshot
      // (daily/weekly/monthly) writes straight into the EXISTING meter
      // signals, so the usage meter updates the instant the turn finishes —
      // no refresh. No-op for an older backend that omits the windows.
      applyLiveQuotaSnapshot(event);
      break;

    case 'usage':
      // Surface the model id via the global signal so the UI can show a
      // fallback hint when resilience routed to a different model than
      // requested. Token/cost details are stored only on the admin hook's
      // local message state today; if customer surfaces start needing them,
      // attach to the store message via a dedicated mutator.
      if (event.model_id) setLastUsedModel(event.model_id);
      break;

    case 'finish':
      deltaBuffer.forceFlush();
      // Ion-as-Insights P1 (Task 10): a turn that opened an Ion transaction
      // (see the tool-output-available case above) commits it here, with a
      // human label built from the commands applied this turn. Commit keeps
      // the state changes and lets the attribution card (ActiveFiltersHeader)
      // offer Revert until the user makes a manual change of their own.
      commitIonTransactionIfOpen(messageId);
      markDone(messageId, { reason: event.reason || 'stop' });
      break;

    case 'error':
      deltaBuffer.forceFlush();
      // A turn that errored out mid-stream may have applied some but not all
      // of its explorer_commands — roll the whole half-applied turn back
      // rather than leaving the Explorer in an inconsistent state. This is
      // still an OPEN (uncommitted) transaction — rollbackOpenTransaction(),
      // not revertIonTransaction() (the latter is for the attribution card,
      // post-commit — see commandLayer.js).
      if (ionTransactionStartedFor.has(messageId)) {
        rollbackOpenTransaction();
        ionTransactionStartedFor.delete(messageId);
        ionCommandTally.delete(messageId);
      }
      markError(messageId, {
        code: event.code || 'unknown',
        message: event.message || 'Unknown error',
        recoverable: !!event.recoverable,
      });
      break;

    default:
      // Forward-compat: unknown event types are logged once but otherwise
      // silently ignored so the frontend never crashes on a backend upgrade.
      if (event.type) {
        console.info('[useChatStream] ignoring unknown event type:', event.type);
      }
  }
}

/**
 * Read X-Quota-* response headers and update the store. Called as soon as
 * the response head arrives, before the body stream starts.
 *
 * `used`/`limit` are Insights Tokens (whole numbers), not raw USD — the
 * backend (`chat/router.py::_build_quota_headers`) stamps these customer-
 * facing legacy headers in tokens for every caller (DEC-6, audit
 * 2026-07-17). Only ratios (`used/limit`) are derived here; never rendered
 * as an absolute currency amount.
 */
function readQuotaHeaders(response) {
  const used = parseInt(response.headers.get('X-Quota-Used') || '', 10);
  const limit = parseInt(response.headers.get('X-Quota-Limit') || '', 10);
  const resetAt = response.headers.get('X-Quota-Reset-At');
  if (Number.isFinite(used) || Number.isFinite(limit) || resetAt) {
    updateQuota({ used, limit, resetAt });
  }
}

/**
 * Send a chat message and consume the streaming response.
 *
 * @param {string} text - The user's prompt.
 * @param {object} [options]
 * @param {object} [options.contextHint] - { page, cellId, ... } passed to backend.
 * @param {string} [options.attachmentId] - Pre-uploaded attachment reference.
 * @param {string} [options.conversationId] - Existing conversation to extend.
 * @param {boolean} [options.onboarding] - Declares this turn as the S4 onboarding
 *   hero turn. The SERVER decides whether it's actually token-exempt (granted
 *   once per user, lifetime) — this only asserts intent in the request body.
 * @param {boolean} [options.example] - Local render hint only (never sent to
 *   the server): marks the optimistic user bubble as an example prompt so it
 *   doesn't read as the user's own words. The backend persists the same
 *   flag itself, derived from `onboarding`.
 *
 * @returns {Promise<{messageId: string, status: 'complete' | 'aborted' | 'error'}>}
 */
export async function sendChatMessage(text, options = {}) {
  if (!text || !text.trim()) return null;

  // Default to the conversation id persisted in the store so callers don't
  // have to thread it through; the response's `X-Conversation-Id` header will
  // overwrite it via the auth interceptor on every roundtrip, so subsequent
  // sends naturally extend the same conversation.
  const {
    contextHint = null,
    attachmentId = null,
    conversationId = currentConversationId.peek(),
    // When the text is an ask_user dialog answer, don't render it as a separate
    // user bubble — the (collapsed) ask_user card already shows the pick.
    askUserAnswer = false,
    // S4 onboarding hero turn: declares intent only — the SERVER decides
    // whether the turn is token-exempt. `example` marks the auto-sent
    // canonical prompt so its bubble never reads as the user's own words
    // (the backend persists the same flag; this is the optimistic local one).
    onboarding = false,
    example = false,
    // Inactivity watchdog budget (ms). See STREAM_IDLE_TIMEOUT_MS.
    idleTimeoutMs = STREAM_IDLE_TIMEOUT_MS,
  } = options;

  // 1. Local message bookkeeping happens BEFORE the network call so the UI
  //    shows the user message immediately and the empty assistant frame
  //    starts streaming the first character with no flash.
  const userMeta = {
    ...(askUserAnswer ? { askUserAnswer: true } : {}),
    ...(example ? { example: true } : {}),
  };
  appendUserMessage(text.trim(), Object.keys(userMeta).length ? userMeta : null);
  const assistantMsg = startAssistantMessage();

  const ac = new AbortController();
  abortController.value = ac;
  const deltaBuffer = createDeltaBuffer();

  // Inactivity watchdog (see STREAM_IDLE_TIMEOUT_MS). Armed just before the
  // read loop and re-armed on every chunk that arrives, so it only fires on
  // real silence. On expiry it does BOTH: abort the fetch (stops the request)
  // and cancel the reader (unblocks the `for await`, which would otherwise sit
  // on a promise the abort does not necessarily reject). The `stalled` flag is
  // what turns the resulting quiet end-of-stream into a recoverable
  // `stream_stalled` error instead of a silently "complete" empty answer.
  let stalled = false;
  let idleTimer = null;
  let activeReader = null;
  const clearIdleTimer = () => {
    if (idleTimer != null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };
  const armIdleTimer = () => {
    clearIdleTimer();
    if (!(idleTimeoutMs > 0)) return;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      stalled = true;
      try {
        ac.abort();
      } catch (e) {
        /* noop */
      }
      // After `ac.abort()` the fetch body is already errored, so `cancel()`
      // returns a promise REJECTED with AbortError — the try/catch below only
      // covers a synchronous throw. Swallow the rejection too, or every stall
      // logs "Uncaught (in promise) AbortError".
      try {
        Promise.resolve(activeReader?.cancel()).catch(() => {});
      } catch (e) {
        /* noop */
      }
    }, idleTimeoutMs);
  };
  const markStalled = () => {
    // Complete (don't strand) any Ion transaction the turn opened, exactly as
    // the manual-abort path does.
    commitIonTransactionIfOpen(assistantMsg.id);
    markError(assistantMsg.id, {
      code: 'stream_stalled',
      message: t('ion.error.streamStalled'),
      recoverable: true,
    });
    return { messageId: assistantMsg.id, status: 'error' };
  };

  // Demo-mode short-circuit: when `?ion_demo=<branch>` is in the URL,
  // replay the local NDJSON fixture instead of hitting the backend. Lets
  // BA showcase the F1 Sales-Selection flow on staging BEFORE TS' tools
  // ship. See frontend/assets/js/services/ionDemoMode.js.
  if (isDemoActive()) {
    try {
      for await (const event of streamDemoEvents()) {
        if (ac.signal.aborted) {
          // C2 fix: complete (don't strand) any Ion transaction this turn
          // opened before the abort — see commitIonTransactionIfOpen above.
          commitIonTransactionIfOpen(assistantMsg.id);
          markAborted(assistantMsg.id);
          return { messageId: assistantMsg.id, status: 'aborted' };
        }
        dispatchEvent(event, { messageId: assistantMsg.id, deltaBuffer });
      }
      deltaBuffer.forceFlush();
      // Demo fixture should always end with `finish`, but guard against
      // a truncated payload the same way the real-stream path does.
      const finalMsg = window.__BATEMO_ION__?.chatMessages
        .peek?.()
        .find((m) => m.id === assistantMsg.id);
      if (finalMsg && finalMsg.status === 'streaming') {
        markDone(assistantMsg.id, { reason: 'stop' });
      }
      return { messageId: assistantMsg.id, status: 'complete' };
    } catch (err) {
      deltaBuffer.forceFlush();
      console.error('[useChatStream] demo replay failed:', err);
      markError(assistantMsg.id, {
        code: 'demo_replay_failed',
        message: err.message || 'Demo replay failed',
        recoverable: true,
      });
      return { messageId: assistantMsg.id, status: 'error' };
    } finally {
      if (abortController.peek() === ac) {
        abortController.value = null;
      }
    }
  }

  try {
    const response = await authenticatedFetch(CHAT_STREAM_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
      },
      body: JSON.stringify({
        text: text.trim(),
        conversationId,
        attachmentId,
        contextHint,
        onboarding,
      }),
      signal: ac.signal,
    });

    readQuotaHeaders(response);

    if (!response.ok) {
      // Backend uses {detail: {code, message}} for chat-specific 4xx; fall
      // back to plain text for older/unhandled responses. 403s flip the
      // global access state so the launcher self-hides for the rest of the
      // session — admins toggling the kill-switch shouldn't require a
      // refresh on the user's side.
      const parsed = await parseChatErrorBody(response);
      if (response.status === 403) {
        recordAccessDenial(parsed.code);
      }
      markError(assistantMsg.id, {
        code: parsed.code || 'http_' + response.status,
        message: parsed.message || `HTTP ${response.status}`,
        recoverable: response.status >= 500 || response.status === 429,
      });
      return { messageId: assistantMsg.id, status: 'error' };
    }

    if (!response.body) {
      // Should not happen on a real fetch but worth guarding for tests/SSR.
      markError(assistantMsg.id, {
        code: 'no_body',
        message: 'Server returned no stream body.',
      });
      return { messageId: assistantMsg.id, status: 'error' };
    }

    const reader = response.body.getReader();
    activeReader = reader;
    try {
      armIdleTimer();
      for await (const event of parseNdjsonStream(reader, { onActivity: armIdleTimer })) {
        dispatchEvent(event, { messageId: assistantMsg.id, deltaBuffer });
      }
    } finally {
      clearIdleTimer();
      try {
        reader.releaseLock();
      } catch (e) {
        /* noop */
      }
    }

    deltaBuffer.forceFlush();

    // The watchdog cancelled the reader — the loop above ended quietly, so the
    // stall has to be reported here rather than through the catch below.
    if (stalled) return markStalled();

    // If the stream ended without an explicit `finish` event, mark the
    // message complete anyway so the UI exits the streaming state.
    const finalMsg = window.__BATEMO_ION__?.chatMessages
      .peek?.()
      .find((m) => m.id === assistantMsg.id);
    if (finalMsg && finalMsg.status === 'streaming') {
      markDone(assistantMsg.id, { reason: 'stop' });
    }

    return { messageId: assistantMsg.id, status: 'complete' };
  } catch (err) {
    deltaBuffer.forceFlush();
    if (err.name === 'AbortError') {
      // The watchdog aborts too — a stall is an error, not a user-initiated
      // stop, so it must not be reported as `aborted`.
      if (stalled) return markStalled();
      // C2 fix: complete (don't strand) any Ion transaction this turn opened
      // before the abort — see commitIonTransactionIfOpen above.
      commitIonTransactionIfOpen(assistantMsg.id);
      markAborted(assistantMsg.id);
      return { messageId: assistantMsg.id, status: 'aborted' };
    }
    console.error('[useChatStream] stream failed:', err);
    markError(assistantMsg.id, {
      code: 'network',
      message: err.message || 'Network error',
      recoverable: true,
    });
    return { messageId: assistantMsg.id, status: 'error' };
  } finally {
    clearIdleTimer();
    if (abortController.peek() === ac) {
      abortController.value = null;
    }
  }
}

/**
 * playRecordedFlow — the capability-gallery's replay entry point (Guided
 * Tour Redesign, Part D). Plays a recording from CAPABILITY_RECORDINGS
 * (ionCapabilityRecordings.js) into the REAL conversation, through the same
 * dispatchEvent()/command-layer path a live turn uses.
 *
 * Deliberately independent of ionDemoMode's isDemoActive() gate: it reads
 * neither the `?ion_demo=` URL flag nor the `ion_demo_replay` localStorage
 * opt-in, and does not set them either — the gallery is reachable by every
 * user, unconditionally, so it must not go through (or touch the meaning
 * of) the sales-demo double-gate. isDemoActive() keeps meaning exactly what
 * it means for ionDemoMode's own callers.
 *
 * Reuses the exact building blocks sendChatMessage's demo branch uses above
 * — appendUserMessage, startAssistantMessage, dispatchEvent, the delta
 * buffer — so a replay drives the real command layer
 * (beginIonTransaction/commit happen INSIDE dispatchEvent's
 * 'tool-output-available'/'finish' cases, unchanged) and produces the same
 * `Ion: … · REVERT` attribution card a live turn does. The recorded question
 * is appended with `{ example: true }`, reusing S4's existing marker
 * (ChatMessage.jsx renders `message.meta.example`) rather than inventing a
 * second one.
 *
 * No network call is made anywhere in this function — the events come from
 * a bundled NDJSON string (CAPABILITY_RECORDINGS[id].fixture). That is also
 * why a replay can never consume the user's one exempt onboarding turn: this
 * path never sends `{ onboarding: true }` to the server, because it never
 * talks to the server at all.
 *
 * Not every event in a captured turn may be replayed — see
 * REPLAY_SUPPRESSED_EVENTS below.
 *
 * @param {string} id - key into CAPABILITY_RECORDINGS
 * @returns {Promise<{messageId: string, status: 'complete'|'error'}|null>}
 *   null if `id` doesn't name a known recording.
 */
/**
 * Event types a replay must NEVER dispatch, whatever a fixture happens to
 * contain.
 *
 * A captured turn carries the recording ACCOUNT's numbers, but these two
 * events are statements about the VIEWER's account: dispatchEvent's `quota`
 * case calls updateQuota() (the Insights-token meter) and its `usage` case
 * calls setLastUsedModel(). Dispatching them would move a viewer's token
 * meter to a stranger's figures and relabel their last-used model — turning
 * the gallery's one promise ("it costs nothing") into a visible lie, on the
 * exact surface that promise is about.
 *
 * Suppressed in the PLAYER rather than stripped when capturing: this is a
 * property of replaying any recording, not of one fixture, so a future
 * capture made by someone who never read this file is still safe. The lines
 * stay in the fixture on purpose — they document what the real turn cost.
 */
const REPLAY_SUPPRESSED_EVENTS = new Set(['quota', 'usage']);

export async function playRecordedFlow(id) {
  const recording = CAPABILITY_RECORDINGS[id];
  if (!recording) {
    console.error('[useChatStream] playRecordedFlow: unknown recording id:', id);
    return null;
  }

  // Loaded BEFORE anything is written to the store: a dynamic import can
  // fail (chunk 404 after a redeploy), and failing here leaves the
  // conversation untouched instead of stranding a half-built exchange with an
  // empty assistant bubble. See ionCapabilityRecordings.js on why the
  // fixtures are lazy — ~208 KB that would otherwise sit in a shared chunk.
  let fixtureText;
  try {
    fixtureText = await recording.loadFixture();
  } catch (e) {
    console.error('[useChatStream] playRecordedFlow: fixture failed to load:', id, e);
    return null;
  }

  appendUserMessage(recording.prompt, { example: true });
  const assistantMsg = startAssistantMessage();
  const deltaBuffer = createDeltaBuffer();

  const lines = fixtureText.split('\n').filter((l) => l.trim().length > 0);
  try {
    for (const line of lines) {
      let event;
      try {
        event = JSON.parse(line);
      } catch (e) {
        console.warn('[useChatStream] skipping malformed recording line:', e);
        continue;
      }
      if (REPLAY_SUPPRESSED_EVENTS.has(event?.type)) continue;
      dispatchEvent(event, { messageId: assistantMsg.id, deltaBuffer });
    }
    deltaBuffer.forceFlush();

    // Recordings should always end with `finish`, but guard against a
    // truncated fixture the same way the live-stream path does.
    const finalMsg = chatMessages.peek().find((m) => m.id === assistantMsg.id);
    if (finalMsg && finalMsg.status === 'streaming') {
      markDone(assistantMsg.id, { reason: 'stop' });
    }

    return { messageId: assistantMsg.id, status: 'complete' };
  } catch (err) {
    deltaBuffer.forceFlush();
    console.error('[useChatStream] recorded flow replay failed:', err);
    markError(assistantMsg.id, {
      code: 'recording_replay_failed',
      message: err.message || 'Recording replay failed',
      recoverable: true,
    });
    return { messageId: assistantMsg.id, status: 'error' };
  }
}

async function safeReadText(response) {
  try {
    return await response.text();
  } catch (e) {
    return '';
  }
}

/**
 * Format a quota reset timestamp into a short, friendly clause for the
 * customer-facing message ("at 14:30", "tomorrow at 09:00"). Falls back to a
 * vague "shortly" when the timestamp is missing or unparseable so we never
 * surface a raw ISO string or a dangling placeholder.
 */
function formatQuotaReset(iso) {
  if (!iso) return t('ion.error.quota.reset.now');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('ion.error.quota.reset.now');
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  // Same calendar day → "at HH:MM"; otherwise disambiguate the day.
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `at ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();
  if (isTomorrow) return `tomorrow at ${time}`;
  const day = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  return `on ${day} at ${time}`;
}

/**
 * Build the customer-facing message for a quota 429. The backend detail is
 * `{code:'quota_exceeded', scope, reset_at}` (no human message) — we localise
 * per scope here. `scope` is one of 'daily'/'weekly'/'monthly' (USD cost
 * budgets — monthly added 2026-07, Ion AI limits system), or unknown →
 * generic. (2026-07-17: the 'daily_requests' per-day AI-question-COUNT scope
 * was removed along with the backend request-count cap — the USD windows are
 * the sole enforcement mechanism now.) DEMO's monthly scope's `resetAt` is
 * the literal string "never" (one-time account ceiling, no rolling reset) —
 * handled by `formatQuotaReset`, not a special case here.
 */
export function quotaMessageForScope(scope, resetAt) {
  // DEMO's monthly scope never resets (§2.3) — the backend sends the literal
  // string "never" rather than a date; that gets its own full-sentence copy
  // instead of awkwardly interpolating "never" into "it resets {reset}".
  if (scope === 'monthly' && resetAt === 'never') {
    return t('ion.error.quota.monthly.demo');
  }
  const reset = formatQuotaReset(resetAt);
  const key =
    scope === 'daily'
      ? 'ion.error.quota.daily'
      : scope === 'weekly'
        ? 'ion.error.quota.weekly'
        : scope === 'monthly'
          ? 'ion.error.quota.monthly'
          : 'ion.error.quota.generic';
  return t(key, { reset });
}

/**
 * Parse the FastAPI error envelope. Returns {code, message} where `code` is
 * a machine-readable string (one of: free_tier, no_subscription, inactive,
 * disabled_by_admin, kill_switch, quota_exceeded, http_<status>, unknown)
 * and `message` is human-readable.
 */
async function parseChatErrorBody(response) {
  try {
    const body = await response.clone().json();
    const detail = body?.detail;
    if (typeof detail === 'string') {
      return { code: 'http_' + response.status, message: detail };
    }
    if (detail && typeof detail === 'object') {
      // Quota 429: the backend sends {code:'quota_exceeded', scope, reset_at}
      // with no human message — map the scope to a friendly, localised line
      // (uses the reset time when present). `daily`/`weekly`/`monthly` are
      // the USD cost budgets (the sole enforcement mechanism, 2026-07-17).
      if (detail.code === 'quota_exceeded') {
        return {
          code: 'quota_exceeded',
          message: quotaMessageForScope(detail.scope, detail.reset_at ?? detail.resetAt),
        };
      }
      return {
        code: detail.code || 'http_' + response.status,
        message: detail.message || detail.code || `HTTP ${response.status}`,
      };
    }
  } catch {
    /* non-JSON body */
  }
  const text = await safeReadText(response);
  return {
    code: 'http_' + response.status,
    message: text || `HTTP ${response.status}`,
  };
}

/**
 * Abort the currently-streaming response, if any. Safe to call when nothing
 * is streaming (no-op).
 */
export function stopChatStream() {
  const ac = abortController.peek();
  if (ac && !ac.signal.aborted) {
    try {
      ac.abort();
    } catch (e) {
      console.warn('[useChatStream] abort failed:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// Admin-side hook with self-contained state
// ---------------------------------------------------------------------------

/**
 * useChatStream — NDJSON streaming client for POST /api/v1/chat/stream.
 *
 * Used by the admin dev sandbox (AdminAiTab) where chat state is local to
 * the component, not the global Ion store.
 *
 * Contract:
 *   - conversation_id is assigned on first user message and reused for the
 *     remainder of the session; persists across browser reloads only if the
 *     caller passes `{ persistConversation: true }` (uses sessionStorage).
 *   - message shape: { id, role, content, status, stats?, error? }
 *   - status: 'streaming' | 'complete' | 'aborted' | 'error'
 *   - stats: { ttftMs, totalMs, inputTokens?, outputTokens?, cachedInputTokens?, quotaUsed?, quotaLimit? }
 *
 * Options for a surface on another endpoint with the same NDJSON wire (the
 * Ion Next admin page): `streamUrl` replaces `${getApiBase()}/chat/stream`
 * and `messagesUrl(encodedPublicId)` replaces the history URL. Both default
 * to the customer URLs, so a caller that passes neither is unchanged.
 * `send(text, { profile, answerStyle })` adds `profile` / `answerStyle` (the
 * Ion Next answer style) to the body only when given.
 * `pinArtifacts: false` keeps a history load out of the global chat canvas
 * (chatStore's pinned artifact); the default pins, as before.
 * `loadConversation` resolves `{ ok: true, data }` with the history response.
 */
export function useChatStream({
  persistConversation = false,
  streamUrl,
  messagesUrl,
  pinArtifacts = true,
} = {}) {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [quota, setQuota] = useState(null); // { used, limit, resetAt }
  const [conversationId, setConversationIdState] = useState(
    () => readConversationId(persistConversation) || newId()
  );
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const abortRef = useRef(null);
  const loadSeqRef = useRef(0);

  const setConversationId = useCallback(
    (id) => {
      conversationIdRef.current = id;
      setConversationIdState(id);
      writeConversationId(id, persistConversation);
    },
    [persistConversation]
  );

  // Closing the admin AI tab mid-stream used to leave the POST running and
  // keep pushing deltas into setMessages on an unmounted component. `mounted`
  // is the gate every async continuation below checks before touching state;
  // the cleanup also aborts whatever request is in flight.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    writeConversationId(conversationIdRef.current, persistConversation);
  }, [persistConversation]);

  const updateMessage = useCallback((id, patch) => {
    if (!mountedRef.current) return;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const appendDeltaLocal = useCallback((id, delta) => {
    if (!mountedRef.current) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, content: m.content + delta } : m))
    );
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsStreaming(false);
    setQuota(null);
    setConversationId(newId());
  }, [setConversationId]);

  /**
   * Load a persisted conversation — fetches its full message history and
   * swaps the hook state over to that conversation. Any in-flight stream is
   * aborted first.
   */
  const loadConversation = useCallback(
    async (publicId) => {
      if (!publicId) return { ok: false, error: 'missing_id' };
      abortRef.current?.abort();
      // A load that has been superseded (another conversation clicked) or
      // whose component is gone must not write state when it finally lands.
      const seq = ++loadSeqRef.current;
      const stale = () => !mountedRef.current || loadSeqRef.current !== seq;
      try {
        const response = await authenticatedFetch(
          messagesUrl
            ? messagesUrl(encodeURIComponent(publicId))
            : `${getApiBase()}/chat/conversations/${encodeURIComponent(publicId)}/messages`
        );
        if (stale()) return { ok: false, error: 'cancelled' };
        if (!response.ok) {
          return { ok: false, error: `http_${response.status}` };
        }
        const data = await response.json();
        if (stale()) return { ok: false, error: 'cancelled' };
        // Single shared mapper (chatMessageMapper) — identical shape to the
        // slide-over loader so the two can't drift. A past drift hardcoded
        // toolCalls:[] here's sibling and broke chips on reopen-from-history.
        const hydrated = (data.messages || []).map(mapApiMessage);
        setMessages(hydrated);
        // Re-pin the last renderable artifact so charts/tables reappear in
        // the canvas on reopen — the live `endToolCall` auto-pin never
        // replays on history load (sets pinnedArtifact in chatStore).
        if (pinArtifacts) pinLatestArtifactFromMessages(hydrated);
        setConversationId(data.conversationId || publicId);
        setIsStreaming(false);
        return { ok: true, data };
      } catch (err) {
        return { ok: false, error: err?.message || 'network' };
      }
    },
    [setConversationId, messagesUrl, pinArtifacts]
  );

  const send = useCallback(
    async (text, opts = {}) => {
      const trimmed = text?.trim();
      if (!trimmed || isStreaming) return;

      const userMessage = {
        id: newId(),
        role: 'user',
        content: trimmed,
        status: 'complete',
      };
      const assistantMessage = {
        id: newId(),
        role: 'assistant',
        content: '',
        status: 'streaming',
        stats: {},
        // Tool calls accumulated during this assistant turn. Same shape as
        // the customer-side chatStore so the existing ToolCallCard /
        // ToolStatusInline components render without modification.
        toolCalls: [],
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      const t0 = performance.now();
      let ttftMs = null;
      let finalQuota = null;
      let sawFinish = false;

      try {
        const body = {
          conversationId: conversationIdRef.current,
          text: trimmed,
        };
        if (opts.model) body.model = opts.model;
        if (opts.profile) body.profile = opts.profile;
        if (opts.answerStyle) body.answerStyle = opts.answerStyle;

        const response = await authenticatedFetch(streamUrl || `${getApiBase()}/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        // The component may have gone away while the request was in flight.
        if (!mountedRef.current) return;

        // Quota headers arrive regardless of the body stream — snapshot them up front.
        const headerQuota = readQuotaFromHeaders(response.headers);
        if (headerQuota) setQuota(headerQuota);

        // Canonical conversation id from the server.
        const serverConvId = response.headers.get('X-Conversation-Id');
        if (serverConvId && serverConvId !== conversationIdRef.current) {
          setConversationId(serverConvId);
        }

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');
          const httpError = {
            code: `http_${response.status}`,
            message: errorBody || `${response.status} ${response.statusText}`,
            recoverable: response.status >= 500,
          };
          updateMessage(assistantMessage.id, { status: 'error', error: httpError });
          return;
        }

        if (!response.body) {
          updateMessage(assistantMessage.id, {
            status: 'error',
            error: { code: 'no_stream', message: 'Response has no body stream.' },
          });
          return;
        }

        // Reuses the shared parser instead of a second hand-rolled loop. The
        // loop this replaces never flushed the trailing buffer after `done`,
        // so a stream whose last line carried no '\n' lost that event — and
        // when the lost event was `finish`, the `!sawFinish` branch below
        // relabelled a perfectly complete answer as `truncated_stream`.
        const reader = response.body.getReader();
        try {
          for await (const event of parseNdjsonStream(reader)) {
            // Unmounted mid-stream: stop dispatching into dead state.
            if (!mountedRef.current) break;

            if (!KNOWN_EVENTS.has(event.type)) {
              console.debug('[useChatStream] unknown event, ignoring:', event.type);
              continue;
            }

            if (event.type === 'text-delta') {
              if (ttftMs === null) ttftMs = Math.round(performance.now() - t0);
              appendDeltaLocal(assistantMessage.id, event.delta || '');
              continue;
            }

            if (event.type === 'quota') {
              finalQuota = {
                used: event.used,
                limit: event.limit,
                resetAt: event.reset_at ?? event.resetAt,
              };
              setQuota(finalQuota);
              continue;
            }

            if (event.type === 'usage') {
              const u = {
                modelId: event.model_id,
                inputTokens: event.input_tokens,
                outputTokens: event.output_tokens,
                cachedInputTokens: event.cached_input_tokens ?? 0,
                cacheWriteTokens: event.cache_write_tokens ?? 0,
                costUsd: event.cost_usd != null ? Number(event.cost_usd) : null,
                latencyMs: event.latency_ms ?? null,
              };
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id ? { ...m, usage: u, stats: { ...m.stats, ...u } } : m
                )
              );
              continue;
            }

            if (event.type === 'finish') {
              sawFinish = true;
              const totalMs = Math.round(performance.now() - t0);
              updateMessage(assistantMessage.id, {
                status: 'complete',
                stats: {
                  ...assistantMessage.stats,
                  ttftMs,
                  totalMs,
                  finishReason: event.reason,
                  quotaUsed: finalQuota?.used ?? headerQuota?.used,
                  quotaLimit: finalQuota?.limit ?? headerQuota?.limit,
                },
              });
              continue;
            }

            if (event.type === 'error') {
              updateMessage(assistantMessage.id, {
                status: 'error',
                error: {
                  code: event.code,
                  message: event.message,
                  recoverable: !!event.recoverable,
                },
              });
              continue;
            }

            // Tool-call lifecycle — admin sandbox now mirrors what the
            // customer surface does. We mutate `toolCalls` on the assistant
            // message in local state; the same ToolCallCard / ToolStatusInline
            // components consume that array unchanged.
            if (event.type === 'tool-input-start') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id
                    ? {
                        ...m,
                        toolCalls: [
                          ...m.toolCalls,
                          {
                            id: event.tool_call_id,
                            name: event.tool_name,
                            input: event.input || {},
                            output: null,
                            status: 'running',
                            startedAt: Date.now(),
                            endedAt: null,
                            isError: false,
                          },
                        ],
                      }
                    : m
                )
              );
              continue;
            }

            if (event.type === 'tool-input-available') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id
                    ? {
                        ...m,
                        toolCalls: m.toolCalls.map((t) =>
                          t.id === event.tool_call_id ? { ...t, input: event.input || {} } : t
                        ),
                      }
                    : m
                )
              );
              continue;
            }

            if (event.type === 'tool-output-available') {
              // Admin dev sandbox — local component state only. Intentionally
              // does NOT execute explorer_command (Ion-as-Insights P1);
              // explorer commands only run through the customer-facing
              // dispatchEvent() below, which drives the global commandLayer.
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id
                    ? {
                        ...m,
                        toolCalls: m.toolCalls.map((t) =>
                          t.id === event.tool_call_id
                            ? {
                                ...t,
                                output: event.output,
                                isError: !!event.is_error,
                                status: 'done',
                                endedAt: Date.now(),
                              }
                            : t
                        ),
                      }
                    : m
                )
              );
              continue;
            }

            // data-source, figure, kpi-group: not yet rendered by this
            // admin surface, but we log so we know when the backend starts
            // emitting them.
            console.debug('[useChatStream] received (not yet rendered):', event);
          }
        } finally {
          try {
            reader.releaseLock();
          } catch (e) {
            /* noop */
          }
        }

        // Stream ended without an explicit finish event — treat as abnormal close.
        if (!sawFinish && mountedRef.current) {
          setMessages((prev) => {
            const current = prev.find((m) => m.id === assistantMessage.id);
            if (!current || current.status !== 'streaming') return prev;
            const totalMs = Math.round(performance.now() - t0);
            return prev.map((m) =>
              m.id === assistantMessage.id
                ? {
                    ...m,
                    status: 'error',
                    error: {
                      code: 'truncated_stream',
                      message: 'Stream ended without a finish event.',
                      recoverable: true,
                    },
                    stats: { ...m.stats, ttftMs, totalMs },
                  }
                : m
            );
          });
        }
      } catch (err) {
        const aborted = err?.name === 'AbortError';
        updateMessage(assistantMessage.id, {
          status: aborted ? 'aborted' : 'error',
          error: aborted
            ? null
            : { code: 'network', message: err?.message || 'Network error', recoverable: true },
          stats: {
            ttftMs,
            totalMs: Math.round(performance.now() - t0),
          },
        });
      } finally {
        abortRef.current = null;
        if (mountedRef.current) setIsStreaming(false);
      }
    },
    [appendDeltaLocal, isStreaming, setConversationId, streamUrl, updateMessage]
  );

  return {
    messages,
    isStreaming,
    quota,
    conversationId,
    send,
    abort,
    reset,
    loadConversation,
  };
}

/**
 * useChatModels — fetches the allowlist + default. Cached for the session.
 */
export function useChatModels() {
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authenticatedFetch(`${getApiBase()}/chat/models`);
        if (cancelled) return;
        if (!r.ok) {
          setError(`http_${r.status}`);
          return;
        }
        const data = await r.json();
        setModels(data.models || []);
        setDefaultModel(data.default || null);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'network');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { models, defaultModel, error };
}

/**
 * useChatConversations — companion hook that fetches the current user's
 * conversation list and exposes a refresh handle.
 */
export function useChatConversations({ autoLoad = true } = {}) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Same reasoning as the streaming hook: the drawer can be closed while a
  // list fetch is in flight, and two quick refreshes can land out of order.
  const mountedRef = useRef(true);
  const refreshSeqRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const seq = ++refreshSeqRef.current;
    const stale = () => !mountedRef.current || refreshSeqRef.current !== seq;
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${getApiBase()}/chat/conversations?limit=30`);
      if (stale()) return;
      if (!response.ok) {
        setError(`http_${response.status}`);
        return;
      }
      const data = await response.json();
      if (stale()) return;
      setConversations(data.conversations || []);
    } catch (err) {
      if (!stale()) setError(err?.message || 'network');
    } finally {
      if (!stale()) setLoading(false);
    }
  }, []);

  const remove = useCallback(
    async (publicId) => {
      try {
        const response = await authenticatedFetch(
          `${getApiBase()}/chat/conversations/${encodeURIComponent(publicId)}`,
          { method: 'DELETE' }
        );
        // A 403/500 used to drop the row from the drawer anyway — the
        // conversation was still on the server and came back on the next
        // refresh, which reads as data loss followed by a ghost.
        //
        // `error` is reserved for LIST-LOAD failures: the drawer renders it
        // INSTEAD of the list, so setting it here would hide the list we just
        // refreshed behind a misleading "could not load". The caller gets the
        // outcome through the return value and surfaces it next to the list.
        if (!response?.ok) {
          const code = `http_${response?.status ?? 'error'}`;
          await refresh();
          return { ok: false, error: code };
        }
        if (mountedRef.current) {
          setConversations((prev) => prev.filter((c) => c.conversationId !== publicId));
        }
        return { ok: true };
      } catch (err) {
        await refresh();
        return { ok: false, error: err?.message || 'network' };
      }
    },
    [refresh]
  );

  useEffect(() => {
    if (autoLoad) refresh();
  }, [autoLoad, refresh]);

  return { conversations, loading, error, refresh, remove };
}

function readQuotaFromHeaders(headers) {
  const used = headers.get('X-Quota-Used');
  const limit = headers.get('X-Quota-Limit');
  const resetAt = headers.get('X-Quota-Reset-At');
  if (used === null && limit === null) return null;
  return {
    used: used !== null ? parseInt(used, 10) : null,
    limit: limit !== null ? parseInt(limit, 10) : null,
    resetAt: resetAt || null,
  };
}
